import { toPersianDigits } from '@bookora/shared';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { markAllNotificationsRead } from '@/api/endpoints/notifications';
import { queryKeys } from '@/api/query-keys';
import { useAuth } from '@/auth/token-store';
import { toast } from '@/lib/feedback';

/**
 * «علامت‌گذاری همه به‌عنوان خوانده‌شده» — one definition for the bell's popover and the
 * «همهٔ اعلان‌ها» drawer, which used to carry two copies.
 *
 * The label says what it does. It used to read «خواندن همه» («read them
 * all»), which sounds like it opens something; it marks, it does not open.
 *
 * Both surfaces render it only while something is unread (`unreadTotal > 0`,
 * the bell's own count). The popover used to grey it out with no reason, and
 * the drawer offered it always and then toasted «۰ اعلان خوانده شد».
 */
export const MARK_ALL_READ_LABEL = 'علامت‌گذاری همه به‌عنوان خوانده‌شده';

export function useMarkAllNotificationsRead(): UseMutationResult<
  Awaited<ReturnType<typeof markAllNotificationsRead>>,
  unknown,
  void
> {
  const { activeClinicId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: (result) => {
      // A race with another tab can leave nothing to mark; saying «۰ اعلان»
      // then is noise.
      if (result.updated > 0) toast.success(`${toPersianDigits(result.updated)} اعلان خوانده شد`);
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all(activeClinicId) });
    },
    onError: (error) => toast.error(error),
  });
}
